import TnTextEditor from '@components/tn-text-editor/TnTextEditor';
import TnTypography from '@components/tn-typography/TnTypography';
import { createStyles, makeStyles } from '@material-ui/core';
import React from 'react';

import { NameValuePair } from '../../../_components/NameValuePair';
import CountrySelect from '../../../_components/forms/CountrySelect';
import SelectDropdown, { SelectOption, SelectOptionChangeParam } from '../../../_components/forms/SelectDropdown';
import StreetAddressTextField from '../../../_components/forms/StreetAddressTextField';
import TextField from '../../../_components/forms/TextField';

export const SUMMARY_MAX_LENGTH = 300;

interface CandidateDetailsValues {
  firstName: string;
  lastName: string;
  ssn: string;
  email: string;
  subcontractorFirm: string;
  summary: string;
  additionalInformation: string;
  workAuthorized: SelectOption | null;
  visaRequired: SelectOption | null;
  meetingAvailability: string;
  linkedIn: string;
  phoneNumber: string;
  phoneType: SelectOption | null;
  country: NameValuePair | null;
  streetAddress: string;
  city: string;
  state: NameValuePair | null;
  postalCode: string;
  shoreTypePreferences: SelectOption[] | null;
  workLocationTypePreferences: SelectOption[] | null;
}

interface CandidateDetailsSelectOptions {
  workAuthorized: SelectOption[];
  visaRequired: SelectOption[];
  phoneType: SelectOption[];
  state: NameValuePair[];
  shoreType: SelectOption[];
  workLocationType: SelectOption[];
}

interface CandidateDetailsChangeHandlers {
  shoreTypePreferencesOnChange: (option: SelectOptionChangeParam) => void;
  workLocationTypePreferencesOnChange: (option: SelectOptionChangeParam) => void;
  firstNameOnChange: (value: string) => void;
  lastNameOnChange: (value: string) => void;
  ssnOnChange: (value: string) => void;
  emailOnChange: (value: string) => void;
  subcontractorFirmOnChange: (value: string) => void;
  summaryOnChange: (value: string) => void;
  additionalInformationOnChange: (value: string) => void;
  workAuthorizedOnChange: (value: SelectOptionChangeParam) => void;
  visaRequiredOnChange: (value: SelectOptionChangeParam) => void;
  meetingAvailabilityOnChange: (value: string) => void;
  linkedInOnChange: (value: string) => void;
  phoneNumberOnChange: (value: string) => void;
  phoneTypeOnChange: (value: SelectOptionChangeParam) => void;
  countryOnChange: (value: NameValuePair | null, stateOptions: NameValuePair[]) => void;
  streetAddressOnChange: (value: string) => void;
  cityOnChange: (value: string) => void;
  stateOnChange: (value: SelectOptionChangeParam) => void;
  postalCodeOnChange: (value: string) => void;
}

interface Props {
  values: CandidateDetailsValues;
  options: CandidateDetailsSelectOptions;
  changeHandlers: CandidateDetailsChangeHandlers;
  formDisabled?: boolean;
  streetAddressAutocompleteSelect?: (suggestion: any) => void;
  errors: Record<string, string>;
  touchedFields: Record<string, boolean>;
  onFieldBlur: (field: string) => void;
}

export default function CandidateDetails({
  values,
  options,
  changeHandlers,
  formDisabled = false,
  streetAddressAutocompleteSelect,
  errors,
  touchedFields,
  onFieldBlur,
}: Readonly<Props>) {
  const classes = useStyles();

  return (
    <div className={classes.container}>
      <div className={classes.bodyContainer}>
        {/* Section 1: Candidate Information */}
        <div className={classes.labelContainer}>
          <TnTypography bold variant="body">
            Candidate Information
          </TnTypography>
        </div>

        <div className={classes.bodyElement}>
          <TextField
            required
            name="firstName"
            label="First Name"
            value={values.firstName}
            onChange={changeHandlers.firstNameOnChange}
            disabled={formDisabled}
            characterLimit={50}
            showCharacterLimit
            error={errors.firstName}
            touched={touchedFields.firstName}
            onBlur={() => onFieldBlur('firstName')}
          />
        </div>
        <div className={classes.bodyElement}>
          <TextField
            required
            name="lastName"
            label="Last Name"
            value={values.lastName}
            onChange={changeHandlers.lastNameOnChange}
            disabled={formDisabled}
            characterLimit={50}
            showCharacterLimit
            error={errors.lastName}
            touched={touchedFields.lastName}
            onBlur={() => onFieldBlur('lastName')}
          />
        </div>
        <div className={classes.bodyElement}>
          <TextField
            required
            name="ssn"
            label="Last 4 digits of SSN"
            value={values.ssn}
            onChange={changeHandlers.ssnOnChange}
            characterLimit={4}
            error={errors.ssn}
            touched={touchedFields.ssn}
            onBlur={() => onFieldBlur('ssn')}
          />
        </div>
        <div className={classes.bodyElement}>
          <TextField
            required
            name="email"
            label="Email"
            value={values.email}
            onChange={changeHandlers.emailOnChange}
            error={errors.email}
            touched={touchedFields.email}
            onBlur={() => onFieldBlur('email')}
          />
        </div>
        <div className={classes.bodyElement}>
          <TextField
            disabled={formDisabled}
            name="subcontractorFirm"
            label="Subcontractor Firm"
            value={values.subcontractorFirm}
            onChange={changeHandlers.subcontractorFirmOnChange}
          />
        </div>
        <div className={classes.bodyElement}>
          {/* Placeholder for layout balance (no Diversity Certification per ticket) */}
          <div />
        </div>

        {/* Summary - full width */}
        <div className={classes.fullWidthElement}>
          <TextField
            required
            isMultiline
            numRows={4}
            characterLimit={SUMMARY_MAX_LENGTH}
            showCharacterLimit
            name="summary"
            label="Candidate Summary"
            placeholder="Write a brief summary about the candidate"
            value={values.summary}
            onChange={changeHandlers.summaryOnChange}
            error={errors.summary}
            touched={touchedFields.summary}
            onBlur={() => onFieldBlur('summary')}
          />
        </div>

        {/* Additional Information - full width */}
        <div className={classes.fullWidthElement}>
          <TnTypography bold variant="caption">
            Additional Information
          </TnTypography>
          <TnTextEditor
            title="Additional Information"
            value={values.additionalInformation}
            onTextChange={changeHandlers.additionalInformationOnChange}
          />
        </div>

        {/* Section 2: Candidate Status */}
        <div className={classes.labelContainer}>
          <TnTypography bold variant="body">
            Candidate Status
          </TnTypography>
        </div>
        <span />
        <div className={classes.statusRowContainer}>
          <div className={classes.statusElement}>
            <TnTypography variant="caption">
              Is this candidate legally authorized to work in the United States?
            </TnTypography>
            <SelectDropdown
              required
              valueNotChip
              isClearable={false}
              name="workAuthorized"
              label="Work Authorized"
              placeholder="Select Work Authorization Status"
              options={options.workAuthorized}
              value={values.workAuthorized}
              onChange={changeHandlers.workAuthorizedOnChange}
              error={errors.workAuthorized}
              touched={touchedFields.workAuthorized}
              showErrorBeforeBlur={touchedFields.workAuthorized}
              onBlur={() => onFieldBlur('workAuthorized')}
            />
          </div>
          <div className={classes.statusElement}>
            <TnTypography variant="caption">
              Will this candidate now, or in the future, require sponsorship for employment visa status (e.g., H-1B Visa
              status, etc.) to work legally in the United States?
            </TnTypography>
            <SelectDropdown
              required
              valueNotChip
              isClearable={false}
              name="visaRequired"
              label="Visa Required"
              placeholder="Select Visa Requirement Status"
              options={options.visaRequired}
              value={values.visaRequired}
              onChange={changeHandlers.visaRequiredOnChange}
              error={errors.visaRequired}
              touched={touchedFields.visaRequired}
              showErrorBeforeBlur={touchedFields.visaRequired}
              onBlur={() => onFieldBlur('visaRequired')}
            />
          </div>
        </div>

        <span />

        <div className={classes.bodyElement}>
          <SelectDropdown
            required
            isMulti
            name="shoreTypePreferences"
            label="Shore Type Preferences"
            options={options.shoreType}
            value={values.shoreTypePreferences}
            onChange={changeHandlers.shoreTypePreferencesOnChange}
            error={errors.shoreTypePreferences}
            touched={touchedFields.shoreTypePreferences}
            showErrorBeforeBlur={touchedFields.shoreTypePreferences}
            onBlur={() => onFieldBlur('shoreTypePreferences')}
          />
        </div>
        <div className={classes.bodyElement}>
          <SelectDropdown
            required
            isMulti
            name="workLocationTypePreferences"
            label="Work Location Type Preferences"
            options={options.workLocationType}
            value={values.workLocationTypePreferences}
            onChange={changeHandlers.workLocationTypePreferencesOnChange}
            error={errors.workLocationTypePreferences}
            touched={touchedFields.workLocationTypePreferences}
            showErrorBeforeBlur={touchedFields.workLocationTypePreferences}
            onBlur={() => onFieldBlur('workLocationTypePreferences')}
          />
        </div>

        <span />

        {/* Section 3: Contact Information */}
        <div className={classes.labelContainer}>
          <TnTypography bold variant="body">
            Contact Information
          </TnTypography>
        </div>
        <div className={classes.bodyElement}>
          <TextField
            required
            disabled={formDisabled}
            name="meetingAvailability"
            label="Meeting Availability"
            value={values.meetingAvailability}
            onChange={changeHandlers.meetingAvailabilityOnChange}
            placeholder="e.g. MWF between 1pm ET - 7pm ET"
            error={errors.meetingAvailability}
            touched={touchedFields.meetingAvailability}
            onBlur={() => onFieldBlur('meetingAvailability')}
          />
        </div>
        <div className={classes.bodyElement}>
          <TextField
            disabled={formDisabled}
            name="linkedIn"
            label="LinkedIn"
            value={values.linkedIn}
            onChange={changeHandlers.linkedInOnChange}
            placeholder="e.g. https://www.linkedin.com/in/yourname"
          />
        </div>
        <div className={classes.bodyElement}>
          <TextField
            required
            disabled={formDisabled}
            name="phoneNumber"
            label="Phone Number"
            value={values.phoneNumber}
            onChange={changeHandlers.phoneNumberOnChange}
            characterLimit={10}
            error={errors.phoneNumber}
            touched={touchedFields.phoneNumber}
            onBlur={() => onFieldBlur('phoneNumber')}
          />
        </div>
        <div className={classes.bodyElement}>
          <SelectDropdown
            valueNotChip
            disabled={formDisabled}
            name="phoneType"
            label="Phone Type"
            options={options.phoneType}
            value={values.phoneType}
            onChange={changeHandlers.phoneTypeOnChange}
          />
        </div>

        {/* Country - full width */}
        <div className={classes.fullWidthElement}>
          <CountrySelect
            required
            label="Country"
            value={values.country}
            disabled={formDisabled}
            onChange={changeHandlers.countryOnChange}
            error={errors.country}
            touched={touchedFields.country}
            showErrorBeforeBlur={touchedFields.country}
            onBlur={() => onFieldBlur('country')}
          />
        </div>
        <span />

        <div className={classes.bodyElement}>
          <StreetAddressTextField
            label="Street Address"
            disabled={formDisabled || !values.country}
            selectedCountry={values.country?.value ? values.country.value : undefined}
            onChange={changeHandlers.streetAddressOnChange}
            value={values.streetAddress}
            characterLimit={300}
            onAddressSelect={streetAddressAutocompleteSelect}
          />
        </div>
        <div className={classes.bodyElement}>
          <TextField
            disabled={formDisabled || !values.country}
            name="city"
            label="City"
            required
            value={values.city}
            onChange={changeHandlers.cityOnChange}
            error={errors.city}
            touched={touchedFields.city}
            onBlur={() => onFieldBlur('city')}
          />
        </div>
        <div className={classes.bodyElement}>
          <SelectDropdown
            required={values.country?.value === 'US'}
            valueNotChip
            name="state"
            label="State/Region/Province"
            options={options.state as SelectOption[]}
            value={values.state}
            disabled={formDisabled || !values.country}
            onChange={changeHandlers.stateOnChange}
            error={errors.state}
            touched={touchedFields.state}
            showErrorBeforeBlur={touchedFields.state}
            onBlur={() => onFieldBlur('state')}
          />
        </div>
        <div className={classes.bodyElement}>
          <TextField
            disabled={formDisabled || !values.country}
            name="postalCode"
            label="Postal Code"
            value={values.postalCode}
            onChange={changeHandlers.postalCodeOnChange}
          />
        </div>
      </div>
    </div>
  );
}

const useStyles = makeStyles(() =>
  createStyles({
    container: {
      padding: '8px 24px 24px',
    },
    bodyContainer: {
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'wrap',
    },
    bodyElement: {
      width: 'calc(50% - 8px)',
      '&:nth-child(even)': {
        marginRight: 8,
      },
      '&:nth-child(odd)': {
        marginLeft: 8,
      },
    },
    labelContainer: {
      width: '100%',
      marginBottom: 16,
      marginTop: 16,
    },
    fullWidthElement: {
      width: '100%',
    },
    statusRowContainer: {
      display: 'flex',
      flexDirection: 'row',
      gap: '16px',
      width: '100%',
    },
    statusElement: {
      width: '50%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
    },
  }),
);
